export const voiceProcessingProfile = Object.freeze({
  channelCount: 1,
  sampleRateHz: 48_000,
  browserEchoCancellation: true,
  browserNoiseSuppression: true,
  browserAutoGainControl: true,
  // Gain after AEC also raises its residual loudspeaker signal.
  postEchoCancellationGain: 1,
});

export type EnhancedVoiceTrack = Readonly<{
  track: MediaStreamTrack;
  inputTrack: MediaStreamTrack;
  enhanced: boolean;
  dispose: () => Promise<void>;
}>;

/** Ask the browser for its native real-time communications DSP. */
export function voiceAudioConstraints(deviceId?: string): MediaTrackConstraints {
  const constraints: MediaTrackConstraints & Record<string, unknown> = {
    ...(deviceId ? { deviceId: { exact: deviceId } } : {}),
    channelCount: { ideal: voiceProcessingProfile.channelCount },
    sampleRate: { ideal: voiceProcessingProfile.sampleRateHz },
    sampleSize: { ideal: 16 },
    latency: { ideal: 0.02 },
    // Bare true is a required boolean constraint for getUserMedia. An ideal
    // value allowed Chromium to return a microphone track with AEC disabled,
    // which caused a loud acoustic loop on speakers.
    echoCancellation: voiceProcessingProfile.browserEchoCancellation,
    noiseSuppression: voiceProcessingProfile.browserNoiseSuppression,
    autoGainControl: voiceProcessingProfile.browserAutoGainControl,
    googEchoCancellation: true,
    googEchoCancellation2: true,
    googNoiseSuppression: true,
    googNoiseSuppression2: true,
    googAutoGainControl: true,
    googAutoGainControl2: true,
    googHighpassFilter: true,
    googTypingNoiseDetection: true,
  };
  const supported = navigator.mediaDevices?.getSupportedConstraints?.() as MediaTrackSupportedConstraints & Record<string, boolean> | undefined;
  if (supported?.voiceIsolation) constraints.voiceIsolation = true;
  return constraints;
}

export async function createEnhancedVoiceTrack(inputTrack: MediaStreamTrack): Promise<EnhancedVoiceTrack> {
  inputTrack.contentHint = "speech";
  const strictProcessing: MediaTrackConstraints = {
    channelCount: { ideal: voiceProcessingProfile.channelCount },
    sampleRate: { ideal: voiceProcessingProfile.sampleRateHz },
    echoCancellation: { exact: true },
    noiseSuppression: { ideal: true },
    autoGainControl: { ideal: true },
  };
  try {
    await inputTrack.applyConstraints(strictProcessing);
  } catch {
    // Older WebViews do not accept exact constraints on an existing track.
    // Keep all three browser DSP stages enabled with their compatible form.
    await inputTrack.applyConstraints({
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    }).catch(() => undefined);
  }
  // Keep the original capture track. MediaStreamAudioDestination creates a
  // synthetic source, while make-up gain amplifies any residual loudspeaker
  // return. Native AGC raises quiet speech without breaking the AEC reference.
  let disposed = false;
  return {
    track: inputTrack,
    inputTrack,
    enhanced: true,
    dispose: async () => {
      if (disposed) return;
      disposed = true;
      inputTrack.stop();
    },
  };
}
